import { useMatches, useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { useMemo } from 'react';

import {
	getNavigationParamValue,
	hasNavigationParam,
	useNavigationLinkParams,
} from '#src/utils/use-navigation-link-params.js';
import { useAbilityContext } from '#state/contexts/ability-context.js';
import { AddCommandBarProvider } from '../components/add-command-bar-provider.js';

import type { MongoAbility } from '@casl/ability';
import type { AnyRoute, LinkComponentProps } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { CommandBarProviderProps } from '../command-bar-types.js';

export const PAGE_NAV_LABEL_PREFIX = 'Go to';

export interface PageNavigationProviderRouteConfig {
	/**
	 * Label shown in the command bar to navigate to this route.
	 */
	label: string;

	/**
	 * Keywords for search matching. These are aliases _in addition to the `label`_ to help the user find the route.
	 *
	 * Choose a minimal subset of aliases that the user may search for when thinking of this route.
	 *
	 * For example, if the label is "Pinout Editor", you might want to include:
	 * - "pinouts" (plural) but not "pinout" (singular) because the singular is already implicitly included the by the label.
	 * - "pins" but not "pin" because the singular is already implicitly included the by the label.
	 */
	keywords?: string[];
}

/**
 * Discovered route with navigation metadata.
 */
interface DiscoveredRoute {
	/** Full route path (e.g., '/resolution-editor/$projectId') */
	fullPath: string;

	/** Display name for the navigation command */
	label: ReactNode;

	/** Keywords for search matching */
	keywords: string[];

	/** Parameter names required by this route (e.g., ['projectId']) */
	requiredParams: string[];

	/** Params to use when navigating to this route */
	params?: Record<string, any>;

	/** Optional CASL ability requirement */
	authorize?: (ability: MongoAbility) => boolean;
}

/**
 * Extract parameter names from a route path.
 * E.g., '/resolution-editor/$projectId' => ['projectId']
 */
function extractParamNames(path: string): string[] {
	const paramRegex = /\$([a-zA-Z0-9_]+)/g;
	const params: string[] = [];
	let match: RegExpExecArray | null;

	while ((match = paramRegex.exec(path)) !== null) {
		const paramName = match[1];
		if (paramName !== undefined) {
			params.push(paramName);
		}
	}

	return params;
}

/**
 * Recursively traverse the route tree to find all routes with navigation metadata.
 */
function discoverNavigableRoutes(route: AnyRoute, parentPath = ''): DiscoveredRoute[] {
	const routes: DiscoveredRoute[] = [];

	// Build the full path for this route
	const fullPath = `${parentPath}/${route.path ?? ''}`;

	// Check if this route has navigation metadata
	if (route.options.staticData?.navigation) {
		const { label, keywords = [] } = route.options.staticData.navigation;
		const requiredParams = extractParamNames(fullPath);

		routes.push({
			fullPath,
			label,
			keywords,
			requiredParams,
			authorize: route.options.staticData.authorize,
		});
	}

	// Recursively process child routes
	const children = route.children as AnyRoute[] | undefined;
	for (const child of children ?? []) {
		routes.push(...discoverNavigableRoutes(child, fullPath));
	}

	return routes;
}

export interface PageNavigationProviderConfig {
	route?: RegisterRouteProps;
	defaultRelevance?: number;
}

function PageNavigationProvider({ onClose, route }: CommandBarProviderProps & PageNavigationProviderConfig) {
	const navigate = useNavigate();
	const router = useRouter();
	const matches = useMatches();
	const params = useParams({ strict: false });
	const linkParams = useNavigationLinkParams();
	const ability = useAbilityContext();

	// Get the current route path
	const currentRoutePath = matches.at(-1)?.fullPath ?? '';

	// Filter routes to only show those where all required params are available and user has permission
	const availableRoutes: DiscoveredRoute[] = useMemo(() => {
		if (route) {
			return [
				{
					fullPath: route.to,
					label: route.label,
					keywords: route.keywords ?? [],
					requiredParams: extractParamNames(route.to),
					params: typeof route.params === 'boolean' ? undefined : route.params,
				},
			];
		}

		const allRoutes = discoverNavigableRoutes(router.routeTree);

		return allRoutes.filter((discoveredRoute) => {
			if (discoveredRoute.fullPath === currentRoutePath) {
				return false;
			}

			// Check if all required params are available in the URL or navigation context
			const hasRequiredParams = discoveredRoute.requiredParams.every((paramName) =>
				hasNavigationParam(paramName, params as Record<string, unknown>, linkParams),
			);

			if (!hasRequiredParams) {
				return false;
			}

			// Check if user has the required ability (if specified)
			if (discoveredRoute.authorize) {
				return discoveredRoute.authorize(ability);
			}

			return true;
		});
	}, [route, router.routeTree, currentRoutePath, params, linkParams, ability]);

	// Don't render if no routes to navigate to
	if (availableRoutes.length === 0) {
		return null;
	}

	return (
		<>
			{availableRoutes.map((route) => {
				// Build params object with only the required params for this route
				return (
					<Command.Item
						key={route.fullPath}
						keywords={route.keywords}
						onSelect={() => {
							const routeParams: Record<string, string> = route.params ?? {};
							for (const paramName of route.requiredParams) {
								if (routeParams[paramName] !== undefined) {
									continue;
								}

								const paramValue = getNavigationParamValue(paramName, params as Record<string, unknown>, linkParams);
								if (paramValue !== undefined) {
									routeParams[paramName] = paramValue;
								}
							}

							void navigate({ to: route.fullPath, params: routeParams });
							onClose();
						}}
					>
						{typeof route.label === 'string' ?
							<>
								{PAGE_NAV_LABEL_PREFIX} {route.label}
							</>
						:	route.label}
					</Command.Item>
				);
			})}
		</>
	);
}

type RegisterRouteProps = {
	label: ReactNode;
	keywords?: string[];
} & Required<Pick<LinkComponentProps, 'to' | 'params'>>;

/**
 * Component that registers the page navigation provider with the command bar.
 * Place this component in pages where navigation between project-scoped pages should be available.
 *
 * This component automatically detects the current route and available parameters from the router,
 * so no props are needed. It will show navigation options to all routes that have the same parameters
 * as the current route.
 */
export function RegisterPageNavigationProvider({
	route,
	group,
	defaultRelevance,
}: {
	route?: RegisterRouteProps;
	group?: string;
	defaultRelevance?: number;
}) {
	const provider = useMemo(() => {
		return {
			id: 'page-navigation',
			component: PageNavigationProvider,
			getRelevance: defaultRelevance !== undefined ? () => defaultRelevance : undefined,
			props: { route, defaultRelevance },
			group: group ?? 'Page Navigation',
		};
	}, [defaultRelevance, group, route]);

	return <AddCommandBarProvider provider={provider} />;
}
